import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Lock, Mail } from 'lucide-react';
import { authApi, api } from '../utils/api';
import { useAuthStore } from '../stores/authStore';
import { useAppSettingsStore } from '../stores/appSettingsStore';
import TurnstileWidget from '../components/TurnstileWidget';
import EnterpriseLoginVisual from '../components/login/EnterpriseLoginVisual';
import ProductWordmark from '../components/ProductWordmark';
import { resolveImageUrl } from '../utils/url';
import { PORTAL_BASE } from '../config/portalPaths';
import { brandingFooter, hasOperatorBranding } from '../config/brand';
import { merkeAnmeldung } from '../utils/willkommensauftritt';
import '../styles/login.css';

/**
 * Anmeldung für Agenten, Operatoren und Administratoren.
 *
 * ── Zur Gestaltung ──
 * Links die Marke, rechts die Karte; hell, Marineblau und Gold. Die Seite folgt
 * bewusst NICHT dem Dunkelmodus der Anwendung: Sie ist der erste Bildschirm, den
 * ein Kunde sieht, und soll immer gleich aussehen.
 *
 * ── Was der Ablauf mit der Gestaltung macht ──
 * Der Entwurf zeigt E-Mail und Passwort in einem Zug. Die Anwendung fragt aber
 * ZUERST nur die E-Mail: `POST /auth/check-email` entscheidet, ob hier überhaupt
 * jemand richtig ist — ein Kundenkontakt wird ins Portal geschickt, ein
 * stillgelegtes Konto abgewiesen. Wer beides gleichzeitig abfragt, lässt einen
 * Kunden sein Passwort in die falsche Maske tippen.
 *
 * Die Karte bleibt deshalb wie entworfen und tauscht nur ihren Inhalt:
 *   E-Mail → Passwort → ggf. MFA → ggf. erzwungene MFA-Einrichtung.
 */

const MARINE = '#14274b';

/** Die neun Sprachen der Anwendung. Kürzel für die Umschaltleiste. */
const SPRACHEN: Array<{ code: string; kuerzel: string; name: string }> = [
  { code: 'de', kuerzel: 'DE', name: 'Deutsch' },
  { code: 'en', kuerzel: 'EN', name: 'English' },
  { code: 'fr', kuerzel: 'FR', name: 'Français' },
  { code: 'es', kuerzel: 'ES', name: 'Español' },
  { code: 'pt', kuerzel: 'PT', name: 'Português' },
  { code: 'nl', kuerzel: 'NL', name: 'Nederlands' },
  { code: 'pl', kuerzel: 'PL', name: 'Polski' },
  { code: 'ru', kuerzel: 'RU', name: 'Русский' },
  { code: 'be', kuerzel: 'BE', name: 'Vlaams' },
];

/**
 * Ein Eingabefeld mit dem kreisenden Goldrahmen.
 *
 * Der Rahmen ist ein rotierender Kegelverlauf in einem 1,5px breiten Rand. Er
 * wird sichtbar, sobald das Feld gefüllt ist oder den Fokus hat — die Karte
 * zeigt damit ohne Text an, wie weit man ist.
 */
function Feld({
  id,
  label,
  nebenLabel,
  aktiv,
  children,
}: {
  id: string;
  label: string;
  nebenLabel?: React.ReactNode;
  aktiv: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-[10.5px] font-bold uppercase tracking-[.16em] text-[#52627a]">{label}</label>
        {nebenLabel}
      </div>
      <span className="relative block overflow-hidden rounded-[11px] bg-[#e0e5ee] p-[1.5px]">
        <span
          aria-hidden
          className="asd-frame-fast pointer-events-none absolute left-1/2 top-1/2 aspect-square w-[240%] transition-opacity duration-300"
          style={{
            background:
              'conic-gradient(from 0deg, transparent 0 55%, rgba(201,150,60,.2) 70%, #c9963c 85%, #f6e6bd 92%, transparent 100%)',
            opacity: aktiv ? 1 : 0,
          }}
        />
        {children}
      </span>
    </div>
  );
}

/** Gemeinsame Klassen für die eigentlichen Eingabefelder in einem `Feld`. */
const EINGABE =
  'relative block h-[46px] w-full rounded-[9.5px] border-0 bg-[#fcfdfe] px-[15px] text-[14.5px] text-[#14274b]';

/**
 * Der dunkelblaue Hauptknopf mit dem durchlaufenden Glanz.
 *
 * MUSS auf Modulebene stehen. Innerhalb von `LoginPage` definiert, wäre er bei
 * jedem Rendern ein NEUER Komponententyp — React wirft den Knopf dann bei jedem
 * Tastendruck weg und baut ihn neu auf. Sichtbar wird das erst spät: Wer sich mit
 * der Tastatur bis zum Knopf durchgehangelt hat, verliert den Fokus, sobald sich
 * irgendetwas im Formular ändert.
 */
const Hauptknopf = ({ disabled, children }: { disabled?: boolean; children: React.ReactNode }) => (
  <button
    type="submit"
    disabled={disabled}
    className="relative flex items-center justify-center gap-2 overflow-hidden rounded-[10px] border border-[#14274b] px-[18px] py-[15px] text-[12.5px] font-bold uppercase tracking-[.14em] text-white transition-[transform,translate,box-shadow,border-color] duration-150 hover:-translate-y-px hover:border-[#c9963c] active:translate-y-0 disabled:cursor-not-allowed disabled:border-[#c6cfdd] disabled:text-[#516078] disabled:hover:translate-y-0 disabled:hover:border-[#c6cfdd]"
    style={{
      fontFamily: 'Montserrat, sans-serif',
      background: disabled ? '#d7dee9' : `linear-gradient(180deg, #1c3157, ${MARINE})`,
      boxShadow: disabled ? 'none' : '0 10px 24px -14px rgba(20,39,75,.8)',
    }}
  >
    <span
      aria-hidden
      className="asd-sheen pointer-events-none absolute bottom-0 left-0 top-0 w-[42%]"
      style={{
        background: 'linear-gradient(90deg, transparent, rgba(240,215,155,.42) 45%, rgba(246,230,189,.5) 55%, transparent)',
        filter: 'blur(2px)',
      }}
    />
    <span className="relative flex items-center gap-2">{children}</span>
  </button>
);

const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { loginSuccess, setMustChangePassword } = useAuthStore();
  const appSettings = useAppSettingsStore((s) => s.settings);

  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'email' | 'password'>('email');
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [redirectingToPortal, setRedirectingToPortal] = useState(false);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showDefaultCreds, setShowDefaultCreds] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [bootstrapRenameToken, setBootstrapRenameToken] = useState<string | null>(null);
  const [replacementEmail, setReplacementEmail] = useState('');
  const [ssoConfig, setSsoConfig] = useState<{ sso_available: boolean; tenant_id?: string; client_id?: string } | null>(null);
  const [ssoLoading, setSsoLoading] = useState(false);
  const msalInstanceRef = useRef<any>(null);

  const [showMfaInput, setShowMfaInput] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');

  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState<{ secret: string; qrCode: string } | null>(null);
  const [mfaSetupVerificationCode, setMfaSetupVerificationCode] = useState('');
  const [mfaBackupCodes, setMfaBackupCodes] = useState<string[]>([]);
  const [mfaSetupStep, setMfaSetupStep] = useState<1 | 2>(1);
  const [pendingLoginPayload, setPendingLoginPayload] = useState<any>(null);

  const handleCaptchaToken = useCallback((token: string | null) => {
    setCaptchaToken(token);
  }, []);

  const [setupWizardPending, setSetupWizardPending] = useState(false);

  useEffect(() => {
    api.get('/auth/setup-status').then(res => {
      setShowDefaultCreds(!!res.data.showDefaultCredentials);
      setSetupWizardPending(!res.data.setupWizardCompleted);
    }).catch(() => {});

    api.get('/auth/sso/agent-config').then(res => {
      if (res.data.sso_available) {
        setSsoConfig(res.data);
      }
    }).catch(() => {});
  }, []);

  /**
   * Nach erfolgreicher Anmeldung: wohin? An EINER Stelle, sonst driften die Wege.
   *
   * Hier haengt auch der Merker fuer den einmaligen Aufbau der Konsole. Er gehoert
   * genau hierher: Es gibt vier Wege herein — Passwort, MFA-Code, erzwungene
   * MFA-Einrichtung und SSO — und alle vier laufen durch diese Funktion. An jedem
   * einzelnen gesetzt haette der fuenfte ihn vergessen.
   */
  const weiterNachAnmeldung = (user: any) => {
    if (user?.recovery_scope) {
      navigate('/maintenance-recovery');
      return;
    }
    merkeAnmeldung();
    const isAdmin = user?.role === 'admin';
    if (isAdmin && setupWizardPending) {
      navigate('/setup');
      return;
    }
    navigate(isAdmin || user?.role === 'agent' ? '/agent' : '/tickets');
  };

  const handleSsoLogin = async () => {
    if (!ssoConfig?.sso_available || !ssoConfig.client_id || !ssoConfig.tenant_id) return;
    setSsoLoading(true);
    setErrorMsg(null);

    try {
      const { PublicClientApplication } = await import('@azure/msal-browser');

      if (!msalInstanceRef.current) {
        msalInstanceRef.current = new PublicClientApplication({
          auth: {
            clientId: ssoConfig.client_id,
            authority: `https://login.microsoftonline.com/${ssoConfig.tenant_id}`,
            redirectUri: window.location.origin + '/login',
          },
          cache: { cacheLocation: 'sessionStorage' },
        });
        await msalInstanceRef.current.initialize();
      }

      const loginResponse = await msalInstanceRef.current.loginPopup({
        scopes: ['openid', 'profile', 'email'],
        prompt: 'select_account',
      });

      if (loginResponse?.idToken) {
        const res = await api.post('/auth/sso/agent-callback', {
          idToken: loginResponse.idToken,
          email: loginResponse.account?.username || '',
        });

        if (res.data.success) {
          loginSuccess(res.data.user, res.data.accessToken, res.data.refreshToken);
          setMustChangePassword(false);
          weiterNachAnmeldung(res.data.user);
        }
      }
    } catch (err: any) {
      if (err.errorCode === 'user_cancelled') {
        setErrorMsg(null);
      } else {
        setErrorMsg(err.response?.data?.error || err.message || t('login.sso_anmeldung_fehlgeschlagen', 'SSO-Anmeldung fehlgeschlagen'));
      }
    } finally {
      setSsoLoading(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setErrorMsg(t('login.bitte_geben_sie_eine_gueltige_e_mail_adresse', 'Bitte geben Sie eine gültige E-Mail-Adresse ein.'));
      return;
    }
    setIsCheckingEmail(true);
    setErrorMsg(null);

    try {
      const response = await api.post('/auth/check-email', { email });
      if (response.data.success) {
        const { type } = response.data;
        if (type === 'customer') {
          setRedirectingToPortal(true);
          setTimeout(() => {
            navigate(`${PORTAL_BASE}/login`, { state: { email } });
          }, 1500);
        } else if (type === 'inactive') {
          setErrorMsg(t('login.ihr_account_ist_deaktiviert', 'Ihr Account ist deaktiviert.'));
        } else {
          // Agent / Admin / Operator -> Passwort abfragen
          setStep('password');
        }
      } else {
        setErrorMsg(response.data.error || t('login.fehler_beim_ueberpruefen_der_e_mail_adresse', 'Fehler beim Überprüfen der E-Mail-Adresse.'));
      }
    } catch (err: any) {
      setErrorMsg(err.response?.data?.error || t('login.ein_fehler_ist_aufgetreten', 'Ein Fehler ist aufgetreten.'));
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const [forgotSending, setForgotSending] = useState(false);
  const handleForgotPassword = async () => {
    if (!email) {
      setErrorMsg(t('login.bitte_geben_sie_zuerst_ihre_e_mail_adresse_e', 'Bitte geben Sie zuerst Ihre E-Mail-Adresse ein.'));
      return;
    }
    setForgotSending(true);
    setErrorMsg(null);
    try {
      const res = await authApi.forgotPassword(email, captchaToken || undefined);
      // Das Backend antwortet absichtlich unspezifisch (kein Rueckschluss darauf,
      // ob es das Konto gibt). Die Meldung enthaelt „gesendet" — daran erkennt die
      // Anzeige unten, dass sie blau und nicht rot sein soll.
      setErrorMsg(res.data?.message || t('login.falls_ein_konto_existiert_wurde_eine_e_mail', 'Falls ein Konto existiert, wurde eine E-Mail zum Zurücksetzen gesendet.'));
    } catch {
      setErrorMsg(t('login.falls_ein_konto_existiert_wurde_eine_e_mail', 'Falls ein Konto existiert, wurde eine E-Mail zum Zurücksetzen gesendet.'));
    } finally {
      setForgotSending(false);
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg(null);

    try {
      if (password) {
        const response = await authApi.login(email, undefined, password, captchaToken || undefined);

        if (response.data.success) {
          if (response.data.bootstrapAdminRenameRequired && response.data.renameToken) {
            setBootstrapRenameToken(response.data.renameToken);
            setReplacementEmail('');
            setPassword('');
            return;
          }
          if (response.data.mfaRequired) {
            setMfaToken(response.data.mfaToken);
            setShowMfaInput(true);
            setIsLoading(false);
            return;
          }

          if (response.data.mfaSetupRequired) {
            setMfaToken(response.data.mfaToken);
            setIsLoading(true);
            try {
              const setupRes = await authApi.setupEnforcedMfa(response.data.mfaToken);
              if (setupRes.data.success) {
                setMfaSetupData(setupRes.data);
                setShowMfaSetup(true);
                setMfaSetupStep(1);
              } else {
                setErrorMsg(t('login.mfa_einrichtung_fehlgeschlagen', 'MFA-Einrichtung fehlgeschlagen.'));
              }
            } catch (err: any) {
              setErrorMsg(err.response?.data?.error || t('login.fehler_beim_laden_des_mfa_setups', 'Fehler beim Laden des MFA-Setups'));
            } finally {
              setIsLoading(false);
            }
            return;
          }

          loginSuccess(response.data.user, response.data.accessToken, response.data.refreshToken);
          if (response.data.mustChangePassword && !response.data.user?.recovery_scope) {
            setMustChangePassword(true);
            navigate('/change-password');
          } else {
            setMustChangePassword(false);
            weiterNachAnmeldung(response.data.user);
          }
        }
      } else {
        setErrorMsg(t('login.bitte_geben_sie_ihr_passwort_ein', 'Bitte geben Sie Ihr Passwort ein.'));
      }
    } catch (error: any) {
      setErrorMsg(error.response?.data?.error || t('login.ein_fehler_ist_aufgetreten', 'Ein Fehler ist aufgetreten.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBootstrapRename = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bootstrapRenameToken || !replacementEmail.trim()) return;
    setIsLoading(true);
    setErrorMsg(null);
    try {
      const response = await authApi.completeBootstrapAdmin(bootstrapRenameToken, replacementEmail);
      const changedEmail = response.data.newEmail || replacementEmail.trim().toLowerCase();
      setEmail(changedEmail);
      setReplacementEmail('');
      setBootstrapRenameToken(null);
      setPassword('');
      setCaptchaToken(null);
      setErrorMsg(response.data.message || t('login.e_mail_adresse_erfolgreich_geaendert', 'E-Mail-Adresse erfolgreich geändert. Melden Sie sich jetzt mit der neuen Adresse an.'));
    } catch (error: any) {
      setErrorMsg(error.response?.data?.error || t('login.administratoradresse_konnte_nicht_geaendert_werden', 'Die Administratoradresse konnte nicht geändert werden.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg(null);

    try {
      if (!mfaToken) return;
      const response = await authApi.verifyMfa(mfaCode, mfaToken);

      if (response.data.success) {
        if (response.data.bootstrapAdminRenameRequired && response.data.renameToken) {
          setBootstrapRenameToken(response.data.renameToken);
          setReplacementEmail('');
          setShowMfaInput(false);
          setMfaToken(null);
          setMfaCode('');
          setPassword('');
          return;
        }
        loginSuccess(response.data.user, response.data.accessToken, response.data.refreshToken);
        setMustChangePassword(false);
        weiterNachAnmeldung(response.data.user);
      }
    } catch (error: any) {
      setErrorMsg(error.response?.data?.error || t('login.ungueltiger_oder_abgelaufener_mfa_code', 'Ungültiger oder abgelaufener MFA-Code.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSetupVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaToken || !mfaSetupData || !mfaSetupVerificationCode) return;
    setIsLoading(true);
    setErrorMsg(null);
    try {
      const res = await authApi.enableEnforcedMfa(mfaToken, mfaSetupVerificationCode, mfaSetupData.secret);
      if (res.data.success) {
        setMfaBackupCodes(res.data.backupCodes || []);
        setPendingLoginPayload(res.data);
        setMfaSetupStep(2);
      }
    } catch (error: any) {
      setErrorMsg(error.response?.data?.error || t('login.aktivierung_fehlgeschlagen_code_pruefen', 'Aktivierung fehlgeschlagen. Bitte den Code überprüfen.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSetupComplete = () => {
    if (!pendingLoginPayload) return;
    loginSuccess(pendingLoginPayload.user, pendingLoginPayload.accessToken, pendingLoginPayload.refreshToken);
    setMustChangePassword(false);
    weiterNachAnmeldung(pendingLoginPayload.user);
  };

  const appName = appSettings.app_name || 'NALVRA SERVICE';
  const vendorFooterText = useAppSettingsStore(s => s.vendorFooterText);
  const brandingAllowed = useAppSettingsStore(s => s.brandingAllowed);
  // Eine „gesendet"-Meldung ist eine Bestaetigung, kein Fehler — sie darf nicht rot sein.
  const istHinweis = !!errorMsg && (errorMsg.includes('gesendet') || errorMsg.includes('erfolgreich geändert'));

  return (
    <div
      // `overflow-x-clip`: Der Schein um die Karte liegt 34px AUSSERHALB von ihr.
      // Auf einem 375px-Bildschirm schiebt er das Dokument auf 385px — die Seite
      // laesst sich dann seitwaerts wischen, und der Inhalt wackelt beim Tippen.
      // Geklemmt statt versteckt: `hidden` erzeugte einen Scroll-Container und
      // haette die Sprachleiste mitgenommen.
      className="asd-login asd-enterprise-login grid min-h-dvh items-center overflow-x-clip bg-white"
      style={{
        fontFamily: 'Manrope, system-ui, sans-serif',
      }}
    >
      <EnterpriseLoginVisual
        productLogo
        appName={appName}
        logoUrl={appSettings.logo_url}
        fusszeile={`VERSION ${__APP_VERSION__}`}
        claim={t('login.claim', 'Service Desk, Assets und Lizenzen — eine Datenbasis, ein Prozess.')}
      />

      <div className="relative flex min-h-dvh flex-col items-center justify-center gap-[26px] px-6 py-16 sm:px-16">
        {/* Sprachwahl */}
        <div
          // Auf dem Telefon steht die Leiste IM Fluss ueber der Karte: Neun
          // Kuerzel brechen dort auf zwei Reihen um, und absolut positioniert
          // legten sie sich dabei ueber die Ueberschrift. Ab `sm` sitzt sie
          // wieder oben rechts wie entworfen.
          className="asd-rise flex max-w-full flex-wrap items-center justify-center gap-0.5 rounded-[999px] border border-[#eaeef4] bg-[#fbfcfe] p-1 sm:absolute sm:right-11 sm:top-[34px] sm:max-w-[calc(100%-5.5rem)] sm:justify-end"
          style={{ animationDelay: '3.3s' }}
        >
          {SPRACHEN.map((s) => {
            const gewaehlt = i18n.language === s.code;
            return (
              <button
                key={s.code}
                type="button"
                lang={s.code}
                aria-label={s.name}
                aria-current={gewaehlt ? 'true' : undefined}
                onClick={() => {
                  void i18n.changeLanguage(s.code);
                  // MUSS mitgeschrieben werden: `getInitialLanguage()` in i18n.ts
                  // liest genau diesen Schluessel, und es gibt keinen Erkenner,
                  // der ihn von selbst setzt. Ohne die Zeile faellt die Wahl beim
                  // naechsten Laden zurueck auf Deutsch — ausgerechnet hier, wo
                  // jemand die Sprache VOR der Anmeldung waehlt.
                  try { localStorage.setItem('i18nextLng', s.code); } catch { /* privater Modus */ }
                }}
                className="cursor-pointer rounded-[999px] border-0 px-[9px] py-1.5 text-[10.5px] font-bold tracking-[.1em] transition-colors"
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  color: gewaehlt ? '#ffffff' : '#64748b',
                  background: gewaehlt ? MARINE : 'transparent',
                }}
              >
                {s.kuerzel}
              </button>
            );
          })}
        </div>

        <div className="asd-rise relative w-full max-w-[424px]" style={{ animationDelay: '2.5s' }}>
          {/* Der kreisende Schein hinter der Karte */}
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-[34px] overflow-hidden rounded-[52px]"
            style={{
              maskImage: 'radial-gradient(closest-side, #000 58%, rgba(0,0,0,.45) 76%, transparent 100%)',
              WebkitMaskImage: 'radial-gradient(closest-side, #000 58%, rgba(0,0,0,.45) 76%, transparent 100%)',
            }}
          >
            <div
              className="asd-frame absolute left-1/2 top-1/2 aspect-square w-[190%] opacity-80"
              style={{
                background:
                  'conic-gradient(from 0deg, transparent 0 48%, rgba(201,150,60,.35) 66%, rgba(232,190,110,.9) 82%, rgba(246,230,189,.95) 89%, rgba(201,150,60,.3) 95%, transparent 100%)',
                filter: 'blur(26px)',
              }}
            />
          </div>

          <div
            className="relative w-full overflow-hidden rounded-[18px] border border-[#eceff5] bg-white"
            style={{ boxShadow: '0 24px 60px -34px rgba(20,39,75,.28)' }}
          >
            <div className="px-6 pb-[26px] pt-8 sm:px-[34px]">
              <div className="mb-7 flex flex-col gap-[7px]">
                {appSettings.logo_url ? (
                  <img
                    src={resolveImageUrl(appSettings.logo_url)}
                    alt={appName}
                    className="mb-2 max-h-16 max-w-[220px] self-start object-contain object-left"
                  />
                ) : (
                  appName === 'NALVRA SERVICE' ? <div className="mb-2"><ProductWordmark compact /></div>
                    : <div className="text-[12px] font-bold tracking-[.06em] text-[#9a6a17]">{appName}</div>
                )}
                <h1 className="text-balance text-[23px] font-extrabold tracking-[-.2px] text-[#14274b]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {t('login.willkommen_zurueck', 'Willkommen zurück')}
                </h1>
                <p className="text-pretty text-[13.5px] text-[#52627a]">
                  {t('login.melden_sie_sich_an_ihrem_service_desk_an', 'Melden Sie sich an Ihrem Service Desk an.')}
                </p>
              </div>

              {errorMsg && (
                <div
                  role={istHinweis ? 'status' : 'alert'}
                  className={`mb-5 rounded-[10px] border px-4 py-3 text-[13px] font-medium ${
                    istHinweis
                      ? 'border-[#cfe0f5] bg-[#f2f7fd] text-[#28527f]'
                      : 'border-[#f3ced0] bg-[#fdf4f4] text-[#a63d43]'
                  }`}
                >
                  {errorMsg}
                </div>
              )}

              {/* ── Zwei-Faktor: Code abfragen ── */}
              {bootstrapRenameToken ? (
                <form onSubmit={handleBootstrapRename} className="flex flex-col gap-4">
                  <div className="rounded-[10px] border border-[#d9e3f1] bg-[#f5f8fc] px-4 py-3 text-[12.5px] leading-relaxed text-[#52627a]">
                    {t('login.startkonto_bestaetigt_adresse_festlegen', 'Die Zugangsdaten des Startkontos wurden bestätigt. Legen Sie jetzt die geschäftliche Adresse für den Administrator fest.')}
                  </div>
                  <Feld id="replacementEmail" label={t('login.neue_geschaeftliche_e_mail', 'Neue geschäftliche E-Mail')} aktiv={!!replacementEmail.trim()}>
                    <input
                      id="replacementEmail"
                      name="replacementEmail"
                      type="email"
                      required
                      autoFocus
                      autoComplete="email"
                      value={replacementEmail}
                      onChange={(e) => setReplacementEmail(e.target.value)}
                      className={EINGABE}
                    />
                  </Feld>
                  <Hauptknopf disabled={isLoading || !replacementEmail.trim()}>
                    {isLoading && <Loader2 className="size-4 animate-spin" />}
                    {isLoading ? t('login.adresse_wird_geaendert', 'Adresse wird geändert…') : t('login.adresse_aendern', 'Adresse ändern')}
                  </Hauptknopf>
                  <button
                    type="button"
                    onClick={() => {
                      setBootstrapRenameToken(null);
                      setReplacementEmail('');
                      setStep('email');
                      setEmail('');
                      setErrorMsg(null);
                    }}
                    className="text-[12px] font-semibold text-[#7b8aa3] transition-colors hover:text-[#14274b]"
                  >
                    {t('common.abbrechen', 'Abbrechen')}
                  </button>
                </form>
              ) : showMfaInput ? (
                <form onSubmit={handleMfaSubmit} className="flex flex-col gap-4">
                  <Feld id="mfaCode" label={t('login.zwei_faktor_code_totp', 'Zwei-Faktor-Code (TOTP)')} aktiv={!!mfaCode}>
                    <input
                      id="mfaCode"
                      name="mfaCode"
                      type="text"
                      required
                      autoFocus
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={8}
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)}
                      placeholder="000000"
                      className={`${EINGABE} text-center font-mono text-xl tracking-[.35em]`}
                    />
                  </Feld>
                  <p className="-mt-1 text-[12px] leading-relaxed text-[#8593ac]">
                    {t('login.geben_sie_den_6_stelligen_code_aus_ihrer_aut', 'Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App oder einen Wiederherstellungscode ein.')}
                  </p>

                  <Hauptknopf disabled={isLoading || !mfaCode}>
                    {isLoading && <Loader2 className="size-4 animate-spin" />}
                    {isLoading ? t('login.verifiziere', 'Verifiziere…') : t('login.bestaetigen', 'Bestätigen')}
                  </Hauptknopf>

                  <button
                    type="button"
                    onClick={() => {
                      setShowMfaInput(false);
                      setMfaToken(null);
                      setMfaCode('');
                      setPassword('');
                    }}
                    className="text-center text-[12px] text-[#93a0b5] underline transition-colors hover:text-[#14274b]"
                  >
                    {t('login.zurueck_zum_login', 'Zurück zum Login')}
                  </button>
                </form>
              ) : showMfaSetup ? (
                mfaSetupStep === 1 ? (
                  /* ── Zwei-Faktor: erzwungene Einrichtung ── */
                  <form onSubmit={handleMfaSetupVerify} className="flex flex-col gap-4">
                    <div className="text-center">
                      <h2 className="text-[15px] font-bold text-[#b08a41]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {t('login.mfa_einrichtung_erforderlich', 'MFA-Einrichtung erforderlich')}
                      </h2>
                      <p className="mt-1 text-pretty text-[12.5px] text-[#7b8aa3]">
                        {t('login.ihr_administrator_erfordert_die_einrichtung', 'Ihr Administrator erfordert die Einrichtung der Zwei-Faktor-Authentisierung.')}
                      </p>
                    </div>

                    {mfaSetupData?.qrCode && (
                      <div className="mx-auto flex size-44 items-center justify-center rounded-[12px] border border-[#eceff5] bg-white p-3">
                        <img src={mfaSetupData.qrCode} alt={t('login.qr_code_fuer_die_authenticator_app', 'QR-Code für die Authenticator-App')} className="size-40" />
                      </div>
                    )}

                    <div className="rounded-[10px] border border-[#eceff5] bg-[#fbfcfe] p-3 text-center">
                      <p className="text-[10px] font-bold uppercase tracking-[.16em] text-[#a9b4c6]">
                        {t('login.manueller_schluessel', 'Manueller Schlüssel')}
                      </p>
                      <code className="mt-1 block select-all break-all px-2 font-mono text-[12px] tracking-[.15em] text-[#b08a41]">
                        {mfaSetupData?.secret}
                      </code>
                    </div>

                    <Feld id="mfaSetupCode" label={t('login.sechsstelliger_verifizierungscode', '6-stelliger Verifizierungscode')} aktiv={!!mfaSetupVerificationCode}>
                      <input
                        id="mfaSetupCode"
                        name="mfaSetupCode"
                        type="text"
                        required
                        autoFocus
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={mfaSetupVerificationCode}
                        onChange={(e) => setMfaSetupVerificationCode(e.target.value)}
                        placeholder="000000"
                        className={`${EINGABE} text-center font-mono text-xl tracking-[.35em]`}
                      />
                    </Feld>

                    <Hauptknopf disabled={isLoading || !mfaSetupVerificationCode}>
                      {isLoading && <Loader2 className="size-4 animate-spin" />}
                      {isLoading ? t('login.aktivierung', 'Aktivierung…') : t('login.mfa_aktivieren_weiter', 'MFA aktivieren & weiter')}
                    </Hauptknopf>

                    <button
                      type="button"
                      onClick={() => {
                        setShowMfaSetup(false);
                        setMfaToken(null);
                        setMfaSetupData(null);
                        setMfaSetupVerificationCode('');
                        setPassword('');
                      }}
                      className="text-center text-[12px] text-[#93a0b5] underline transition-colors hover:text-[#14274b]"
                    >
                      {t('login.abbrechen', 'Abbrechen')}
                    </button>
                  </form>
                ) : (
                  /* ── Zwei-Faktor: Wiederherstellungscodes ── */
                  <div className="flex flex-col gap-4">
                    <div className="text-center">
                      <h2 className="text-[15px] font-bold text-[#1f7a52]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {t('login.mfa_erfolgreich_aktiviert', 'MFA erfolgreich aktiviert')}
                      </h2>
                      <p className="mt-1 text-pretty text-[12.5px] text-[#7b8aa3]">
                        {t('login.speichern_sie_diese_einmal_wiederherstellung', 'Speichern Sie diese Einmal-Wiederherstellungscodes gut ab! Sie können sie verwenden, falls Sie den Zugriff auf Ihre App verlieren.')}
                      </p>
                    </div>

                    <div className="rounded-[10px] border border-[#e6dcc4] bg-[#fdfaf3] p-4 text-center font-mono">
                      <div className="flex flex-col gap-2 text-[13px] tracking-[.15em] text-[#14274b]">
                        {mfaBackupCodes.map((code) => (
                          <span key={code} className="select-all tabular-nums">{code}</span>
                        ))}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleMfaSetupComplete}
                      className="rounded-[10px] border border-[#14274b] px-[18px] py-[15px] text-[12.5px] font-bold uppercase tracking-[.14em] text-white transition-transform duration-150 hover:-translate-y-px"
                      style={{ fontFamily: 'Montserrat, sans-serif', background: `linear-gradient(180deg, #1c3157, ${MARINE})` }}
                    >
                      {t('login.einloggen_schliessen', 'Einloggen & Schließen')}
                    </button>
                  </div>
                )
              ) : redirectingToPortal ? (
                /* ── Kundenkontakt: Weiterleitung ins Portal ── */
                <div className="flex flex-col items-center gap-5 py-8 text-center">
                  <div className="relative flex items-center justify-center">
                    <div className="size-16 animate-spin rounded-full border-2 border-[#eceff5] border-t-[#c9963c]" />
                    <Mail className="absolute size-6 text-[#c9963c]" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <h2 className="text-[17px] font-bold text-[#14274b]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {t('login.kunden_account_erkannt', 'Kunden-Account erkannt')}
                    </h2>
                    <p className="mx-auto max-w-xs text-pretty text-[13px] leading-relaxed text-[#7b8aa3]">
                      {t('login.sie_werden_automatisch_zum_kundenportal_weit', 'Sie werden automatisch zum Kundenportal weitergeleitet, um sich dort anzumelden...')}
                    </p>
                  </div>
                </div>
              ) : step === 'email' ? (
                /* ── Schritt 1: E-Mail ── */
                <form onSubmit={handleEmailSubmit} className="flex flex-col gap-4">
                  <Feld id="email" label={t('login.geschaeftliche_e_mail', 'Geschäftliche E-Mail')} aktiv={!!email.trim()}>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      autoFocus
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={EINGABE}
                    />
                  </Feld>

                  {/* Der Entwurf zeigt hier „Auf diesem Gerät angemeldet bleiben".
                      Das Kästchen fehlt bewusst: Die Sitzung liegt heute IMMER im
                      localStorage (zustand `persist`), sie überdauert den Neustart
                      also ohnehin. Ein Kästchen, das nichts abschaltet, sagt dem
                      Benutzer etwas Falsches über seine eigene Sicherheit —
                      schlimmer als keines. Es gehört mit der Sitzungsdauer
                      zusammen gebaut, nicht als Zierde davor. */}

                  <Hauptknopf disabled={isCheckingEmail || !email}>
                    {isCheckingEmail && <Loader2 className="size-4 animate-spin" />}
                    {isCheckingEmail ? t('login.ueberpruefe', 'Überprüfe...') : t('login.weiter', 'Weiter')}
                  </Hauptknopf>

                  {ssoConfig?.sso_available && (
                    <>
                      <div className="mt-1 flex items-center gap-3">
                        <span className="h-px flex-1 bg-[#ecf0f5]" />
                        <span className="text-[10px] font-bold tracking-[.18em] text-[#a9b4c6]">{t('login.oder', 'ODER')}</span>
                        <span className="h-px flex-1 bg-[#ecf0f5]" />
                      </div>
                      <button
                        type="button"
                        onClick={handleSsoLogin}
                        disabled={ssoLoading}
                        className="flex items-center justify-center gap-2.5 rounded-[10px] border border-[#e0e5ee] bg-white px-[18px] py-[13px] text-[13.5px] font-semibold text-[#35486a] transition-colors hover:border-[#c9963c] hover:bg-[#fffdf9] disabled:opacity-60"
                      >
                        {ssoLoading ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <svg viewBox="0 0 21 21" className="size-[15px]" aria-hidden>
                            <path d="M0 0h10v10H0V0z" fill="#F25022" />
                            <path d="M11 0h10v10H11V0z" fill="#7FBA00" />
                            <path d="M0 11h10v10H0V11z" fill="#00A4EF" />
                            <path d="M11 11h10v10H11V11z" fill="#FFB900" />
                          </svg>
                        )}
                        {ssoLoading ? t('login.anmeldung', 'Anmeldung…') : t('login.single_sign_on_entra_id', 'Single Sign-On (Entra ID)')}
                      </button>
                    </>
                  )}
                </form>
              ) : (
                /* ── Schritt 2: Passwort ── */
                <form onSubmit={handleLoginSubmit} className="flex flex-col gap-4">
                  <div className="flex items-center justify-between gap-3 rounded-[10px] border border-[#eceff5] bg-[#fbfcfe] px-4 py-2.5">
                    <span className="flex items-center gap-2.5 overflow-hidden">
                      <Mail className="size-4 shrink-0 text-[#c9963c]" />
                      <span className="truncate text-[13px] font-medium text-[#35486a]">{email}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setStep('email');
                        setPassword('');
                        setErrorMsg(null);
                      }}
                      className="flex shrink-0 items-center gap-1 text-[12px] font-semibold text-[#b08a41] transition-colors hover:text-[#14274b]"
                    >
                      <ArrowLeft className="size-3.5" />
                      {t('login.aendern', 'Ändern')}
                    </button>
                  </div>

                  <Feld
                    id="password"
                    label={t('login.passwort', 'Passwort')}
                    aktiv={!!password}
                    nebenLabel={
                      <button
                        type="button"
                        onClick={handleForgotPassword}
                        disabled={forgotSending}
                        className="text-[11px] font-semibold normal-case tracking-normal text-[#b08a41] transition-colors hover:text-[#14274b] disabled:opacity-50"
                      >
                        {forgotSending ? t('login.sende', 'Sende…') : t('login.zuruecksetzen', 'Zurücksetzen')}
                      </button>
                    }
                  >
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      autoFocus
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••••"
                      className={`${EINGABE} tracking-[.08em]`}
                    />
                  </Feld>

                  {showDefaultCreds && (
                    <p className="-mt-1 text-[11.5px] leading-relaxed text-[#8593ac]">
                      {t('login.erstanmeldung', 'Erstanmeldung:')}{' '}
                      <strong className="font-semibold text-[#35486a]">admin@ticket.local / ElenaMatis1984!</strong>
                    </p>
                  )}

                  <TurnstileWidget route="login" onToken={handleCaptchaToken} />

                  <Hauptknopf disabled={isLoading || !password}>
                    {isLoading && <Loader2 className="size-4 animate-spin" />}
                    {isLoading ? t('login.anmelden_laeuft', 'Anmelden…') : t('login.anmelden', 'Anmelden')}
                  </Hauptknopf>
                </form>
              )}
            </div>

            {/* Fussleiste der Karte. Jede Aussage hier ist im Code belegt:
                aes-256-gcm in utils/encryption.ts, MFA in services/mfaService. Was sich nicht
                belegen laesst, steht hier nicht — diese Zeile liest ein Kaeufer. */}
            <div className="flex items-center justify-between gap-3 border-t border-[#eef1f6] bg-[#fbfcfe] px-6 py-3.5 text-[11.5px] text-[#93a0b5] sm:px-[34px]">
              <span className="flex items-center gap-2">
                <Lock className="size-3 shrink-0 text-[#c9963c]" />
                {t('login.sicherheitszeile_kurz', 'AES-256 · Zwei-Faktor')}
              </span>
            </div>
          </div>
        </div>

        <div className="asd-rise flex flex-wrap items-center justify-center gap-4 text-[11px] tracking-[.04em] text-[#a3aebf]" style={{ animationDelay: '3.1s' }}>
          <span>{brandingFooter({ brandingAllowed, showVendor: appSettings.portal_show_footer_branding,
            hasOperatorBrand: hasOperatorBranding({ appName, logoUrl: appSettings.logo_url }),
            customCopyright: appSettings.custom_copyright_text, operatorName: appName, vendorFooter: vendorFooterText })}</span>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
